import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Mic, MicOff, Send, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';

/**
 * DailyReflectionModal - Private free-expression space.
 * 
 * Philosophy:
 * - NO evaluation
 * - NO correction  
 * - NO optimization
 * - This is NOT content creation
 * - User is thinking out loud
 * 
 * Features:
 * - Free text input (primary)
 * - Optional voice input (secondary)
 * - One optional reflective question (always ignorable)
 */
export function DailyReflectionModal({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [content, setContent] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reflectiveQuestion, setReflectiveQuestion] = useState(null);
  const [showQuestion, setShowQuestion] = useState(true);
  const [moodHint, setMoodHint] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const textareaRef = useRef(null);

  // Get a random question when modal opens
  useEffect(() => {
    if (open && showQuestion) {
      fetchRandomQuestion();
    }
  }, [open]);

  const fetchRandomQuestion = async () => {
    try {
      // Pass current language to get question in correct language
      const res = await apiClient.get('/reflection/question/random', {
        params: { language: i18n.language }
      });
      setReflectiveQuestion(res.data.question);
    } catch (error) {
      // Silent fail - question is optional
      console.error('Failed to fetch question:', error);
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Pass current language to backend
      await apiClient.post('/reflection/create', {
        content: content.trim(),
        mood_hint: moodHint,
        show_question: showQuestion,
        language: i18n.language  // Pass current UI language
      });

      // Simple acknowledgment - no praise, no analysis
      toast.success(t('reflection.saved', 'Saved'));
      
      // Reset and close
      setContent('');
      setMoodHint(null);
      onOpenChange(false);

    } catch (error) {
      console.error('Failed to save reflection:', error);
      toast.error(t('reflection.errorSaving', 'Error saving'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error(t('reflection.micError', 'لا يمكن الوصول للميكروفون'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      if (moodHint) formData.append('mood_hint', moodHint);
      formData.append('show_question', showQuestion.toString());
      formData.append('language', i18n.language);  // Pass current UI language for transcription
      
      await apiClient.post('/reflection/voice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(t('reflection.saved', 'Saved'));
      onOpenChange(false);
      
    } catch (error) {
      console.error('Failed to transcribe:', error);
      toast.error(t('reflection.transcribeError', 'Failed to transcribe audio'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl + Enter to submit
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-xl flex flex-col p-0 gap-0"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header - minimal */}
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-lg font-medium text-gray-800">
            {t('reflection.title', 'تأملي')}
          </DialogTitle>
        </DialogHeader>

        {/* Optional reflective question - very subtle */}
        {showQuestion && reflectiveQuestion && (
          <div className="px-6 py-3 bg-gray-50 border-b">
            <p className="text-sm text-gray-500 italic">
              {reflectiveQuestion}
            </p>
            <button 
              onClick={() => fetchRandomQuestion()}
              className="text-xs text-gray-400 hover:text-gray-600 mt-1 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              {t('reflection.anotherQuestion', 'سؤال آخر')}
            </button>
          </div>
        )}

        {/* Main input area */}
        <div className="p-6 flex-1">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('reflection.placeholder', 'اكتب ما يدور في ذهنك... لا تقييم، لا تصحيح.')}
            className="min-h-[200px] resize-none text-base leading-relaxed border-0 focus:ring-0 p-0 placeholder:text-gray-300"
            disabled={isSubmitting || isRecording}
          />
        </div>

        {/* Optional mood hint - very subtle, not required */}
        <div className="px-6 py-2 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{t('reflection.howAreYou', 'كيف حالك؟')}</span>
            <div className="flex gap-1">
              {[
                { value: 'high', emoji: '🌟', label: t('reflection.mood.high', 'جيد') },
                { value: 'neutral', emoji: '🌤️', label: t('reflection.mood.neutral', 'عادي') },
                { value: 'low', emoji: '🌧️', label: t('reflection.mood.low', 'منخفض') }
              ].map(mood => (
                <button
                  key={mood.value}
                  onClick={() => setMoodHint(moodHint === mood.value ? null : mood.value)}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    moodHint === mood.value 
                      ? 'bg-gray-200 text-gray-700' 
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={mood.label}
                >
                  {mood.emoji}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          {/* Voice input - secondary */}
          <Button
            variant="ghost"
            size="icon"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isSubmitting}
            className={`rounded-full ${isRecording ? 'bg-red-100 text-red-600' : ''}`}
          >
            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </Button>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="gap-2 bg-[hsl(145,25%,55%)] hover:bg-[hsl(145,25%,45%)]"
          >
            <Send className="w-4 h-4" />
            {t('reflection.save', 'حفظ')}
          </Button>
        </div>

        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute inset-0 bg-black/5 flex items-center justify-center">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <span className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
              </div>
              <p className="text-gray-600">{t('reflection.recording', 'جارٍ التسجيل...')}</p>
              <Button 
                onClick={stopRecording}
                variant="outline"
                className="mt-4"
              >
                {t('reflection.stopRecording', 'إيقاف')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DailyReflectionModal;
